import { ReactNode } from 'react';
import { FieldValues } from 'react-hook-form';

import { EMPTY_ARR } from '../constants';
import { Field, Fields, FieldTypes } from '../types/fields';
import { Option } from '../types/utility';
import { WorkflowStepFormFieldBlock } from '../types/workflows';
import { applyTransformFuncs } from '../utils/general';
import { ArrayFieldToBlock, ArrayFieldValue } from './Array.field';
import { booleanFieldToBlock, BooleanFieldValue } from './boolean';
import { CategoryFieldToBlock, CategoryFieldValue } from './Category.field';
import { CurrencyFieldToBlock, CurrencyFieldValue } from './Currency.field';
import { DateFieldToBlock, DateFieldValue } from './Date.field';
import {
  DateMonthFullYearFieldToBlock,
  DateMonthFullYearFieldValue,
} from './DateMonthFullYear.field';
import { DecimalFieldToBlock, DecimalFieldValue } from './Decimal.field';
import { EmailFieldToBlock, EmailFieldValue } from './Email.field';
import { GenderFieldToBlock, GenderFieldValue } from './Gender.field';
import { HtmlFieldToBlock, HtmlFieldValue } from './Html.field';
import { IntegerFieldToBlock, IntegerFieldValue } from './Integer.field';
import {
  NestedSelectFieldToBlock,
  NestedSelectFieldValue,
} from './NestedSelect.field';
import { NumberFieldToBlock, NumberFieldValue } from './Number.field';
import { PercentFieldToBlock, PercentFieldValue } from './Percent.field';
import { PhoneFieldToBlock, PhoneFieldValue } from './Phone.field';
import {
  RepeatedJsonFieldToBlock,
  RepeatedJsonFieldValue,
} from './RepeatedJson.field';
import { SelectFieldToBlock, SelectFieldValue } from './Select.field';
import { TextFieldToBlock, TextFieldValue } from './Text.field';
import { TimeFieldToBlock, TimeFieldValue } from './Time.field';
import { TypeaheadFieldToBlock, TypeaheadFieldValue } from './Typeahead.field';
import { WysiwygFieldToBlock, WysiwygFieldValue } from './Wysiwyg.field';
import { XmlFieldToBlock, XmlFieldValue } from './Xml.field';

/**
 * Important utility functions for creating "Field" forms.
 */

/**
 * @param field
 */
export const commonFieldBlockProps = (
  field: Field<FieldTypes>,
): Pick<
  WorkflowStepFormFieldBlock,
  'name' | 'label' | 'defaultValue' | 'type'
> => ({
  name: field.usePathAsName ? field.path : field.name.replaceAll('.', '~'),
  label: field.label,
  defaultValue: field.value ?? field.defaultValue,
  type: 'form-field',
});

/**
 * Transform the field resource into a block config for BlockRenderer
 *
 * @param field
 */
export function transformFieldIntoBlock(
  field: Field<FieldTypes>,
): WorkflowStepFormFieldBlock {
  switch (field.type) {
    case 'currency':
      return CurrencyFieldToBlock(field as Fields.ComponentMap['currency']);
    case 'number':
      return NumberFieldToBlock(field as Fields.ComponentMap['number']);
    case 'percent':
      return PercentFieldToBlock(field as Fields.ComponentMap['percent']);
    case 'phone':
      return PhoneFieldToBlock(field as Fields.ComponentMap['phone']);
    case 'select':
      return SelectFieldToBlock(field as Fields.ComponentMap['select']);
    case 'repeated_json':
      return RepeatedJsonFieldToBlock(
        field as Fields.ComponentMap['repeated_json'],
      );
    case 'array':
      return ArrayFieldToBlock(field as Fields.ComponentMap['array']);
    case 'time':
      return TimeFieldToBlock(field as Fields.ComponentMap['time']);
    case 'boolean':
      return booleanFieldToBlock(field as Fields.ComponentMap['boolean']);
    case 'date':
      return DateFieldToBlock(field as Fields.ComponentMap['date']);
    case 'decimal':
      return DecimalFieldToBlock(field as Fields.ComponentMap['decimal']);
    case 'email':
      return EmailFieldToBlock(field as Fields.ComponentMap['email']);
    case 'integer':
      return IntegerFieldToBlock(field as Fields.ComponentMap['integer']);
    case 'text':
      return TextFieldToBlock(field as Fields.ComponentMap['text']);
    case 'html':
      return HtmlFieldToBlock(field as Fields.ComponentMap['html']);
    case 'xml':
      return XmlFieldToBlock(field as Fields.ComponentMap['xml']);
    case 'gender':
      return GenderFieldToBlock(field as Fields.ComponentMap['gender']);
    case 'wysiwyg':
      return WysiwygFieldToBlock(field as Fields.ComponentMap['wysiwyg']);
    case 'typeahead':
      return TypeaheadFieldToBlock(field as Fields.ComponentMap['typeahead']);
    case 'date_month_full_year':
      return DateMonthFullYearFieldToBlock(
        field as Fields.ComponentMap['date_month_full_year'],
      );
    case 'category':
      return CategoryFieldToBlock(field as Fields.ComponentMap['category']);
    case 'nested_select':
      return NestedSelectFieldToBlock(
        field as Fields.ComponentMap['nested_select'],
      );

    default:
      throw new Error(`Unknown field type ${field.type}`);
  }
}

/**
 * Render the value of the Field
 *
 * @param field
 */
export const renderFieldValue = (field: Field<FieldTypes>): ReactNode => {
  switch (field.type) {
    case 'currency':
      return (
        <CurrencyFieldValue field={field as Fields.ComponentMap['currency']} />
      );
    case 'number':
      return (
        <NumberFieldValue field={field as Fields.ComponentMap['number']} />
      );
    case 'percent':
      return (
        <PercentFieldValue field={field as Fields.ComponentMap['percent']} />
      );
    case 'phone':
      return <PhoneFieldValue field={field as Fields.ComponentMap['phone']} />;
    case 'select':
      return (
        <SelectFieldValue field={field as Fields.ComponentMap['select']} />
      );
    case 'repeated_json':
      return (
        <RepeatedJsonFieldValue
          field={field as Fields.ComponentMap['repeated_json']}
        />
      );
    case 'array':
      return <ArrayFieldValue field={field as Fields.ComponentMap['array']} />;
    case 'time':
      return <TimeFieldValue field={field as Fields.ComponentMap['time']} />;
    case 'boolean':
      return (
        <BooleanFieldValue field={field as Fields.ComponentMap['boolean']} />
      );
    case 'date':
      return <DateFieldValue field={field as Fields.ComponentMap['date']} />;
    case 'decimal':
      return (
        <DecimalFieldValue field={field as Fields.ComponentMap['decimal']} />
      );
    case 'email':
      return <EmailFieldValue field={field as Fields.ComponentMap['email']} />;
    case 'integer':
      return (
        <IntegerFieldValue field={field as Fields.ComponentMap['integer']} />
      );
    case 'text':
      return <TextFieldValue field={field as Fields.ComponentMap['text']} />;
    case 'html':
      return <HtmlFieldValue field={field as Fields.ComponentMap['html']} />;
    case 'xml':
      return <XmlFieldValue field={field as Fields.ComponentMap['xml']} />;
    case 'gender':
      return (
        <GenderFieldValue field={field as Fields.ComponentMap['gender']} />
      );
    case 'wysiwyg':
      return (
        <WysiwygFieldValue field={field as Fields.ComponentMap['wysiwyg']} />
      );
    case 'typeahead':
      return (
        <TypeaheadFieldValue
          field={field as Fields.ComponentMap['typeahead']}
        />
      );
    case 'date_month_full_year':
      return (
        <DateMonthFullYearFieldValue
          field={field as Fields.ComponentMap['date_month_full_year']}
        />
      );
    case 'category':
      return (
        <CategoryFieldValue field={field as Fields.ComponentMap['category']} />
      );
    case 'nested_select':
      return (
        <NestedSelectFieldValue
          field={field as Fields.ComponentMap['nested_select']}
        />
      );
    default:
      return null;
  }
};

/**
 * Transform all fields into blocks
 *
 * @param fields
 */
export const transformFieldsIntoBlocks = (
  fields: Field<FieldTypes>[],
): WorkflowStepFormFieldBlock[] =>
  fields.map((field) => transformFieldIntoBlock(field));

/**
 * Transform the block "defaultValue" for displaying in an input
 *
 * @param block
 */
const transformBlockValueIn = (
  block: WorkflowStepFormFieldBlock,
): Record<string, any> => {
  const transformFuncs = block.transformFuncsIn;
  return transformFuncs
    ? applyTransformFuncs(block.defaultValue, transformFuncs)
    : block.defaultValue;
};

/**
 * Transform all block "defaultValues" for displaying in a form
 * Important for `react-hook-form` form state
 *
 * @param blocks
 */
export const transformBlockValuesIn = (
  blocks: WorkflowStepFormFieldBlock[],
): Record<string, any> =>
  Object.fromEntries(
    blocks.map((block) => {
      const value = transformBlockValueIn(block);

      return [block.name, value];
    }),
  );

/**
 * Transform a field value using its block config
 *
 * @param fieldValue
 * @param block
 */
const transformBlockValueOut = (
  fieldValue: any,
  block: WorkflowStepFormFieldBlock,
): any => {
  const transformFuncs = block.transformFuncsOut;
  return transformFuncs
    ? applyTransformFuncs(fieldValue, transformFuncs)
    : fieldValue;
};

// @READ API interprets relationship as `documentDefinition.attribute`,
// but we can't use `.` on the f/e for field names because _yup_ will turn it into
// an object (and there's no option to disable that)
export const formatFieldKeyOut = (fieldKey: string): string =>
  fieldKey.replaceAll('~', '.');

const transformMultiSelectBlockValueOut = (
  fieldValue: string[],
  block: WorkflowStepFormFieldBlock,
): FieldValues => {
  const options = (block.componentProps?.options ??
    EMPTY_ARR) as Option<string>[];

  return options.reduce(
    (acc, option) => ({
      ...acc,
      [formatFieldKeyOut(`${block.name}_${option.value}`)]: fieldValue.includes(
        option.value,
      ),
    }),
    {} as FieldValues,
  );
};

/**
 * Transform all form values using their respective block configs
 * Important for saving forms which contain fields
 *
 * @param values
 * @param blocks
 */
export const transformBlockValuesOut = (
  values: FieldValues,
  blocks: WorkflowStepFormFieldBlock[],
): FieldValues =>
  Object.fromEntries(
    Object.entries(values).flatMap(([fieldKey, fieldValue]) => {
      const block = blocks.find((b) => b.name === fieldKey);

      if (!block) {
        return [[fieldKey, fieldValue]];
      }

      if (block.componentProps?.isMulti && Array.isArray(fieldValue)) {
        return Object.entries(
          transformMultiSelectBlockValueOut(fieldValue, block),
        );
      }

      const value = transformBlockValueOut(fieldValue, block);

      return [[formatFieldKeyOut(fieldKey), value]]; // double array because we need flatMap for multiselect values
    }),
  );
